import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { endGivebutterPlusFreeTrial } from 'api/subscriptions';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { isoDateFromServer } from 'helpers/dates';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import MrButterJumping from 'images/mr-butter-jumping.png';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface GivebutterPlusEndTrialDialogProps extends Dialog.DialogProps {}

interface GivebutterPlusEndTrialFormValues {
    dontShowAgain: boolean;
    showEndTrialForm: boolean;
    missingFeatures: boolean;
    technicalProblems: boolean;
    tooExpensive: boolean;
    other: boolean;
    reasons: string[];
    otherReason: string;
}

const GivebutterPlusEndTrialDialog = (props: GivebutterPlusEndTrialDialogProps) => {
    const toast = useToast();
    const { children, onOpenChange, ...rest } = props;
    const { accountId, trialEndsAt } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const {
        hasDismissedGivebutterPlusFreeTrialDialog,
        setHasDismissedGivebutterPlusFreeTrialDialog,
        freeTrialDialogMap,
        setFreeTrialDialogLastSeen,
    } = useLocalStorage(state => state);
    const methods = Form.useGivebutterForm<GivebutterPlusEndTrialFormValues>({
        defaultValues: {
            dontShowAgain: false,
            showEndTrialForm: false,
            missingFeatures: false,
            technicalProblems: false,
            tooExpensive: false,
            other: false,
            otherReason: '',
        },
    });
    const { control, handleSubmit, watch, path, setValue, resetFormButton } = methods;
    const [other, dontShowAgain, showEndTrialForm] = watch([
        'other',
        'dontShowAgain',
        'showEndTrialForm',
    ]);

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: ({
            missingFeatures,
            technicalProblems,
            tooExpensive,
            other,
            otherReason,
        }: GivebutterPlusEndTrialFormValues) => {
            const reasonsArray = [
                missingFeatures && 'missing_features',
                technicalProblems && 'technical_problems',
                tooExpensive && 'too_expensive',
                other && 'other',
            ];
            const reason = reasonsArray.filter(Boolean).join(',');
            return endGivebutterPlusFreeTrial(accountId, {
                reason,
                reason_other: other ? otherReason : undefined,
            });
        },
        onSuccess: () => {
            toast.success('Givebutter Plus trial ended.');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            forceRender();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'An unknown error occurred.');
            resetFormButton();
        },
    });

    const endDateMsg = useMemo(() => {
        if (!trialEndsAt) {
            return null;
        }
        const endsAt = isoDateFromServer(trialEndsAt).toFormat('LLLL dd');
        return `Your account is on a free trial until ${endsAt}`;
    }, [trialEndsAt]);

    if (hasDismissedGivebutterPlusFreeTrialDialog) {
        return null;
    }

    const lastSeen = freeTrialDialogMap[accountId]?.free_trial_dialog_last_seen;
    const lastSeenDate = lastSeen ? isoDateFromServer(lastSeen) : null;
    const now = DateTime.now();
    const daysSinceLastSeen = lastSeenDate ? now.diff(lastSeenDate, 'days').days : null;
    if (daysSinceLastSeen !== null && daysSinceLastSeen < 3) {
        return null;
    }

    return (
        <Dialog.Root
            key={key}
            onOpenChange={open => {
                onOpenChange?.(open);
                if (!open) {
                    if (dontShowAgain) {
                        setHasDismissedGivebutterPlusFreeTrialDialog(true);
                    }
                    setFreeTrialDialogLastSeen(accountId.toString(), now.toISO());
                }
            }}
            {...rest}
        >
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Close />
                    <Form.Provider {...methods}>
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSubmit(values => submit(values))(e);
                            }}
                        >
                            {showEndTrialForm && (
                                <div className="flex flex-col gap-8">
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <div className="text-lg font-semibold">
                                                Why are you ending your trial?
                                            </div>
                                            <div className="text-sm text-gray-600">
                                                Please let us know how we can improve.
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-3">
                                            <Form.Label
                                                className="m-px flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 has-[:checked]:m-0 has-[:checked]:border-2 has-[:checked]:border-blue-500"
                                                name={path('missingFeatures')}
                                            >
                                                Missing features
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={path('missingFeatures')}
                                                />
                                            </Form.Label>
                                            <Form.Label
                                                className="m-px flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 has-[:checked]:m-0 has-[:checked]:border-2 has-[:checked]:border-blue-500"
                                                name={path('technicalProblems')}
                                            >
                                                Technical problems
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={path('technicalProblems')}
                                                />
                                            </Form.Label>
                                            <Form.Label
                                                className="m-px flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 has-[:checked]:m-0 has-[:checked]:border-2 has-[:checked]:border-blue-500"
                                                name={path('tooExpensive')}
                                            >
                                                Too expensive
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={path('tooExpensive')}
                                                />
                                            </Form.Label>
                                            <Form.Label
                                                className="m-px flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-gray-200 p-4 text-sm text-gray-700 has-[:checked]:m-0 has-[:checked]:border-2 has-[:checked]:border-blue-500"
                                                name={path('other')}
                                            >
                                                Other
                                                <Form.UseFormCheckboxInput
                                                    control={control}
                                                    name={path('other')}
                                                />
                                            </Form.Label>
                                            {other && (
                                                <>
                                                    <Form.Label name={path('otherReason')}>
                                                        Could you tell us more?
                                                    </Form.Label>
                                                    <Form.UseFormTextArea
                                                        control={control}
                                                        name={path('otherReason')}
                                                        placeholder="Enter a reason for canceling"
                                                        rows={8}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Button
                                            className="flex-1"
                                            variant="outline"
                                            color="neutral"
                                            onClick={() => setValue('showEndTrialForm', false)}
                                        >
                                            Cancel
                                        </Button>
                                        <Form.SubmitButton
                                            isSubmitting={isLoading}
                                            className="flex-1"
                                        >
                                            Next
                                        </Form.SubmitButton>
                                    </div>
                                </div>
                            )}
                            {!showEndTrialForm && (
                                <div className="flex flex-col gap-6">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <img
                                            src={MrButterJumping}
                                            className="max-w-28"
                                            alt="Mr. Butter jumping for joy"
                                        />
                                        <img
                                            className={'w-full max-w-44'}
                                            src={GivebutterPlusLogo}
                                            alt="Givebutter Plus logo"
                                        />
                                        <div className="text-center text-3xl">{endDateMsg}</div>
                                        <div className="text-center text-gray-600">
                                            Upgrade your account at any time during the trial. If
                                            you choose not to upgrade, your account will lose access
                                            to all Givebutter Plus features.
                                        </div>
                                        <div className="flex items-center gap-3 self-start">
                                            <Form.UseFormCheckboxInput
                                                control={control}
                                                name={path('dontShowAgain')}
                                            />
                                            <Form.Label
                                                className="cursor-pointer text-sm text-gray-700"
                                                name={path('dontShowAgain')}
                                            >
                                                Don't show me this again
                                            </Form.Label>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <Link
                                            to={`/accounts/${accountId}/settings/about/subscription`}
                                        >
                                            <Button variant="solid" className="w-full">
                                                Upgrade to Givebutter Plus
                                            </Button>
                                        </Link>
                                        <Button
                                            variant="transparent"
                                            className="!text-gray-600"
                                            onClick={() => setValue('showEndTrialForm', true)}
                                        >
                                            End trial
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </form>
                    </Form.Provider>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const GivebutterPlusEndTrialDialogTrigger = Dialog.Trigger;
const Root = GivebutterPlusEndTrialDialog;
const Trigger = GivebutterPlusEndTrialDialogTrigger;

export { GivebutterPlusEndTrialDialog, GivebutterPlusEndTrialDialogTrigger, Root, Trigger };
